import { FOOD_PREFERENCE, Roles } from '@common/types/user';
import { GenericActionDialog } from '@components/display/dialogs';
import { StyledMenuItem } from '@components/display/menu';
import { Button } from '@components/utility/buttons';
import { Tabs } from '@components/utility/tabs';
import { useAuth } from '@hooks/useAuth';
import { DirtyFormikListener } from '@hooks/useDirtyFormik';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import { memberSchema } from '@members/schemas';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { alpha, Box, Grid2, IconButton, Menu, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { NotFound } from '@routes/NotFound';
import { useDeleteAccountMutation, useGetAccountByIDQuery, usePatchAccountMutation } from '@services/accounts';
import { handleError, toaster } from '@utils/common';
import { getRoleName, isVictronAdmin } from '@utils/user';
import { Form, FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { MemberDetailsFormValues } from '../types';
import { MemberDetails } from './MemberDetails';
import { MemberEarnedBadges } from './MemberEarnedBadges';
import { MemberEarnedCertificates } from './MemberEarnedCertificates';

enum TabsEnum {
    MemberDetails = '',
    EarnedCertificates = 'earned-certificates',
    EarnedBadges = 'earned-badges',
}

/**
 * TODO: Implement member details page functionality, including edit, discard changes, and delete user actions.
 *  */
export const MemberDetailsPage = () => {
    const [discardChangesDialogOpen, setDiscardChangesDialogOpen] = useState(false);
    const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const { user } = useAuth();

    const navigate = usePrefixedNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = Object.values(TabsEnum).find((value) => value === searchParams.get('tab')) ?? TabsEnum.MemberDetails;

    const { id: user_id } = useParams();
    const { data, isError, isLoading } = useGetAccountByIDQuery({ user_id: Number(user_id) });

    const fullNameParts = data?.full_name?.trim().split(/\s+/).filter(Boolean);
    const nameFromFields = `${data?.first_name ?? ''} ${data?.last_name ?? ''}`.trim();
    const displayName = nameFromFields || data?.full_name || '';
    const isUsingFullName = !nameFromFields && !!data?.full_name;
    const firstInitial = data?.first_name?.[0] ?? fullNameParts?.[0]?.[0] ?? '?';
    const lastInitial =
        data?.last_name?.[0] ??
        (fullNameParts?.length ? fullNameParts[fullNameParts.length - 1]?.[0] : undefined) ??
        '?';

    const [patchAccount] = usePatchAccountMutation();
    const [deleteAccount] = useDeleteAccountMutation();

    const formik = useFormik<MemberDetailsFormValues>({
        initialValues: {
            role: data?.role ?? Roles.DEFAULT,
            company_name: data?.company_name ?? '',
            sales_manager: data?.sales_manager,
            distributor: data?.distributor,
            job_title: data?.job_title ?? '',
            job_title_other: data?.job_title_other ?? '',
            first_name: data?.first_name ?? '',
            last_name: data?.last_name ?? '',
            mobile_phone: data?.mobile_phone ?? '',
            telephone: data?.telephone ?? '',
            fax: data?.fax ?? '',
            email: data?.email ?? '',
            country: data?.country ?? '',
            state: data?.state ?? '',
            city: data?.city ?? '',
            address1: data?.address1 ?? '',
            address2: data?.address2 ?? '',
            language: data?.language ?? 'en',
            food_preference: data?.food_preference ?? FOOD_PREFERENCE.NO_PREFERENCE,
            field_permissions: data?.field_permissions ?? null,
            receive_mailings_targeted_at_professionals: data?.receive_mailings_targeted_at_professionals ?? 0,
            is_eligible_for_extended_warranty: data?.is_eligible_for_extended_warranty ?? false,
            is_news_subscriber: data?.is_news_subscriber ?? false,
        },
        initialStatus: { edit: false },
        enableReinitialize: true,
        validationSchema: memberSchema,
        onSubmit: async (values, helpers) => {
            try {
                await patchAccount({
                    user_id: Number(user_id),
                    data: values,
                }).unwrap();
                helpers.resetForm({ values });
                toaster.success({
                    title: 'Changes Saved Successfully',
                    msg: `${
                        values.first_name && values.last_name ? `${values.first_name} ${values.last_name}` : displayName
                    } information was successfully changed.`,
                });
            } catch (error) {
                handleError('Failed to Save Changes', error);
            }
        },
    });

    const handleCancelClick = () => {
        if (formik.dirty) {
            setDiscardChangesDialogOpen(true);
        } else {
            formik.resetForm();
        }
    };

    const handleDiscardChanges = () => {
        formik.resetForm();
        setDiscardChangesDialogOpen(false);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement> | null) => {
        if (event) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDeleteUser = async () => {
        formik.resetForm();
        setDeleteUserDialogOpen(false);
        try {
            await deleteAccount({ user_id: Number(user_id) }).unwrap();
            navigate('/dashboard/members', { replace: true });
            toaster.success({
                title: 'Member Deleted Successfully',
                msg: `${displayName} successfully deleted.`,
            });
        } catch (error) {
            handleError('Failed to Delete Member', error);
        }
    };

    return isError ? (
        <NotFound />
    ) : (
        <FormikProvider value={formik}>
            <DirtyFormikListener ignoreSearchParams />
            <Box component={Form} onSubmit={formik.handleSubmit} noValidate>
                <Stack py="16px" px="62px" spacing="24px">
                    <Stack
                        direction="row"
                        component="main"
                        bgcolor="white.main"
                        borderRadius="14px"
                        border={1}
                        borderColor={alpha('#000', 0.1)}
                        px="40px"
                        py="22px"
                        justifyContent="flex-end"
                        spacing="12px"
                    >
                        {!formik.status?.edit ? (
                            <Button
                                fullRounded
                                size="medium"
                                onClick={() => formik.setStatus({ edit: true })}
                                loading={isLoading}
                                loadingPosition="start"
                            >
                                Edit Member
                            </Button>
                        ) : (
                            <>
                                <Button.Neutral
                                    variant="contained"
                                    color="neutral-650"
                                    fullRounded
                                    size="medium"
                                    onClick={handleCancelClick}
                                >
                                    Cancel
                                </Button.Neutral>
                                <Button fullRounded type="submit" size="medium">
                                    Save
                                </Button>
                                <Tooltip
                                    title={
                                        user && !isVictronAdmin(user)
                                            ? 'Only Victron Admins can perform this action'
                                            : undefined
                                    }
                                >
                                    <IconButton
                                        size="small"
                                        onClick={handleClick}
                                        disabled={user && !isVictronAdmin(user)}
                                    >
                                        <MoreHorizIcon />
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleClose}
                                    classes={{ paper: 'rounded-lg min-w-[225px]', list: 'p-0' }}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                >
                                    <StyledMenuItem
                                        className="text-signal-warning"
                                        onClick={() => {
                                            setAnchorEl(null);
                                            setDeleteUserDialogOpen(true);
                                        }}
                                    >
                                        Delete
                                    </StyledMenuItem>
                                </Menu>
                                <GenericActionDialog
                                    icon={<PersonOutlinedIcon sx={{ fontSize: 30 }} color="primary" />}
                                    open={deleteUserDialogOpen}
                                    onClose={() => setDeleteUserDialogOpen(false)}
                                    title="Delete Member?"
                                    content={
                                        <Typography fontSize={14} color="gray-600" mt={1}>
                                            Delete {displayName}? This action is <b>permanent</b> and cannot be undone.
                                        </Typography>
                                    }
                                    buttons={[
                                        {
                                            label: 'Delete',
                                            buttonProps: {
                                                color: 'signal-warning',
                                                onClick: handleDeleteUser,
                                            },
                                        },
                                        {
                                            label: 'Cancel',
                                            buttonProps: {
                                                appearance: 'neutral',
                                                size: 'large',
                                                onClick: () => setDeleteUserDialogOpen(false),
                                            },
                                        },
                                    ]}
                                    paperSx={{ borderRadius: '16px' }}
                                />
                                <GenericActionDialog
                                    icon={<PersonOutlinedIcon sx={{ fontSize: 30 }} color="primary" />}
                                    open={discardChangesDialogOpen}
                                    onClose={() => setDiscardChangesDialogOpen(false)}
                                    title="Discard changes?"
                                    content={
                                        <Typography fontSize={14} color="gray-600" mt={1}>
                                            Discard changes? This action is <b>permanent</b> and cannot be undone. All
                                            unsaved actions will be lost.
                                        </Typography>
                                    }
                                    buttons={[
                                        {
                                            label: 'Discard changes',
                                            buttonProps: {
                                                color: 'signal-warning',
                                                onClick: handleDiscardChanges,
                                            },
                                        },
                                        {
                                            label: 'Cancel',
                                            buttonProps: {
                                                appearance: 'neutral',
                                                size: 'large',
                                                onClick: () => setDiscardChangesDialogOpen(false),
                                            },
                                        },
                                    ]}
                                    paperSx={{ borderRadius: '16px' }}
                                />
                            </>
                        )}
                    </Stack>
                    <Grid2
                        container
                        component="header"
                        borderRadius="14px"
                        bgcolor="primary-900.main"
                        py="36px"
                        px="52px"
                        columnSpacing="22.5px"
                    >
                        <Grid2 size="auto">
                            <Box
                                bgcolor={alpha('#FFF', 0.1)}
                                border={1}
                                borderRadius="5.625px"
                                borderColor={alpha('#FFF', 0.2)}
                                width={75}
                                height={75}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                {isLoading ? (
                                    <Skeleton width={50} height={75} sx={{ bgcolor: 'white.main' }} />
                                ) : (
                                    <Typography color="white.main" fontSize={22.5} fontWeight={500}>
                                        {firstInitial}
                                        {lastInitial}
                                    </Typography>
                                )}
                            </Box>
                        </Grid2>
                        <Grid2 size="grow">
                            <Stack spacing="11.25px">
                                {isLoading ? (
                                    <Skeleton width={200} height={33.75} sx={{ bgcolor: 'white.main' }} />
                                ) : (
                                    <Typography
                                        color="white.main"
                                        fontFamily="museo-sans"
                                        fontSize={33.75}
                                        fontWeight={600}
                                        letterSpacing={-0.844}
                                        lineHeight="37.5px"
                                        sx={{ minHeight: '37.5px' }}
                                    >
                                        {displayName}
                                        {isUsingFullName ? (
                                            <Typography
                                                component="span"
                                                color="rgba(255, 255, 255, 0.7)"
                                                fontSize={12}
                                                fontWeight={400}
                                                letterSpacing={0}
                                                sx={{ ml: 1, whiteSpace: 'nowrap' }}
                                            >
                                                <FormattedMessage id="members.fullName" defaultMessage="Full name" />
                                            </Typography>
                                        ) : null}
                                    </Typography>
                                )}
                                <Stack direction="row" spacing="20px">
                                    <Stack direction="row" spacing="5px" alignItems="center">
                                        <WorkOutlineOutlinedIcon sx={{ color: 'white.main', fontSize: 13.3 }} />
                                        {isLoading ? (
                                            <Skeleton sx={{ bgcolor: 'white.main', width: 100, height: 15 }} />
                                        ) : (
                                            <Typography
                                                color="white.main"
                                                fontSize={11.25}
                                                fontWeight={400}
                                                lineHeight="15px"
                                            >
                                                {data?.role && getRoleName(data.role)}
                                            </Typography>
                                        )}
                                    </Stack>
                                    <Stack direction="row" spacing="5px" alignItems="center">
                                        <EmailOutlinedIcon sx={{ color: 'white.main', fontSize: 13.3 }} />
                                        {isLoading ? (
                                            <Skeleton sx={{ bgcolor: 'white.main', width: 100, height: 15 }} />
                                        ) : (
                                            <Typography
                                                color="white.main"
                                                fontSize={11.25}
                                                fontWeight={400}
                                                lineHeight="15px"
                                            >
                                                {data?.email}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Grid2>
                    </Grid2>
                    <Box
                        component="main"
                        bgcolor="white.main"
                        borderRadius="14px"
                        border={1}
                        borderColor={alpha('#000', 0.1)}
                        px="40px"
                        py="22px"
                    >
                        <Tabs
                            value={tab}
                            onChange={(_e, nextTab) => setSearchParams({ tab: nextTab ?? TabsEnum.MemberDetails })}
                        >
                            <Tabs.Tab value={TabsEnum.MemberDetails} label="Member Details" />
                            <Tabs.Tab value={TabsEnum.EarnedCertificates} label="Earned Certificates" />
                            <Tabs.Tab value={TabsEnum.EarnedBadges} label="Earned Badges" />
                        </Tabs>
                        {tab === TabsEnum.MemberDetails ? (
                            <MemberDetails isExact={!!data?.field_permissions.role.reasons.includes('exact_account')} />
                        ) : null}
                        {tab === TabsEnum.EarnedCertificates ? <MemberEarnedCertificates /> : null}
                        {tab === TabsEnum.EarnedBadges ? <MemberEarnedBadges /> : null}
                    </Box>
                </Stack>
            </Box>
        </FormikProvider>
    );
};
